<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { toCalendarDayUtcIso } from "@/helpers/date";
import { match } from "@/helpers/string";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";

import type { ProfileUnavailabilityModalProps, UnavailabilitySlots } from "@/components/modal/Profile/unavailability/types";
import type { ProfileUnavailabilityRefreshPayload } from "@/components/modal/types";
import type { DropdownItemProps } from "@/dsComponents/Dropdown";
import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from "@/types/common";

import Alert from "@/dsComponents/Alert/Alert.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import CheckboxLabel from "@/dsComponents/checkbox/CheckboxLabel.vue";
import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";
import { DropdownItemColors, DropdownItemIconStyle } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import DropdownInput from "@/dsComponents/Dropdown/DropdownInput.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import DropdownSeparator from "@/dsComponents/Dropdown/DropdownSeparator.vue";
import TextareaInputAlt from "@/dsComponents/inputs/TextareaInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import Separator from "@/dsComponents/Separator.vue";

import { UnavailabilityType } from "@/components/modal/Profile/unavailability/types";
import { DayPeriod } from "@/components/modal/types";

interface TypeOption {
    value: UnavailabilityType;
    label: string;
    icon: string;
}

const DATE_FORMAT = "DD/MM/YYYY";
const DISPLAY_DATE_FORMAT = "dd D MMM";
const DEFAULT_DURATION_IN_DAYS = 7;
const MAX_RANGE_IN_YEARS = 1;
const EXCLUDED_REASONS = ["MISSION", "MISSION_SOUPLESSE"];
const DAY_PERIODS = Object.values(DayPeriod);
const TYPE_DROPDOWN_STYLES = { width: "317px" };
const REASON_DROPDOWN_STYLES = { width: "296px" };
const SELECTED_OPTION_PROPS: Partial<DropdownItemProps> = {
    labelColor: DropdownItemColors.black,
    contentRight: {
        icon: "check",
        iconStyle: DropdownItemIconStyle.rounded,
        iconColor: DropdownItemColors.blue,
    },
};

const props = defineProps<ProfileUnavailabilityModalProps>();

const emit = defineEmits<(e: "close") => void>();

const { t } = useI18n();
const configStore = useConfigurationStore();
const { toastError, toastSuccess } = useDefaultToast();

const formatInitialDate = (date: string | undefined, fallback: moment.Moment) =>
    (date ? moment(date).utc(true) : fallback).format(DATE_FORMAT);

const today = moment().startOf("day");
const initialUnavailablePeriods = props.unavailablePeriods ?? [];
const isInitiallyCustom = initialUnavailablePeriods.length > 0 && initialUnavailablePeriods.length < DAY_PERIODS.length;

const startDate = ref(formatInitialDate(props.startDate, today));
const endDate = ref(formatInitialDate(props.endDate, today.clone().add(DEFAULT_DURATION_IN_DAYS, "days")));
const type = ref(isInitiallyCustom ? UnavailabilityType.CUSTOM : UnavailabilityType.FULL_DAY);
const availableSlots = ref(
    Object.fromEntries(
        DAY_PERIODS.map((period) => [period, isInitiallyCustom && !initialUnavailablePeriods.includes(period)])
    ) as UnavailabilitySlots
);
const reason = ref(props.reason ?? "");
const comment = ref(props.comment ?? "");
const reasonSearch = ref("");
const isTypeDropdownOpen = ref(false);
const isReasonDropdownOpen = ref(false);
const isSubmitting = ref(false);
const hasTriedSubmit = ref(false);

const isEditMode = computed(() => !!props.id);

const getMaxEnd = (from: moment.Moment) => from.clone().add(MAX_RANGE_IN_YEARS, "year");

const start = computed(() => (startDate.value ? moment(startDate.value, DATE_FORMAT, true) : null));
const end = computed(() => (endDate.value ? moment(endDate.value, DATE_FORMAT, true) : null));
const maxEnd = computed(() => (start.value?.isValid() ? getMaxEnd(start.value) : null));

const isDateRangeValid = computed(
    () =>
        !!start.value?.isValid() &&
        !!end.value?.isValid() &&
        !end.value.isBefore(start.value) &&
        !end.value.isAfter(maxEnd.value)
);
const hasDateRangeError = computed(() => !!start.value && !!end.value && !isDateRangeValid.value);

const minStartDate = today.toDate();
const minEndDate = computed(() => (start.value?.isValid() ? start.value.toDate() : minStartDate));
const maxEndDate = computed(() => maxEnd.value?.toDate());

const formatDisplayDate = (date: moment.Moment | null) => (date?.isValid() ? date.format(DISPLAY_DATE_FORMAT) : "");
const startLabel = computed(() => formatDisplayDate(start.value));
const endLabel = computed(() => formatDisplayDate(end.value));

const typeOptions = computed<TypeOption[]>(() => [
    { value: UnavailabilityType.FULL_DAY, label: t("modals.profileUnavailability.type.fullDay"), icon: "circle-full-dashed" },
    { value: UnavailabilityType.CUSTOM, label: t("modals.profileUnavailability.type.custom"), icon: "circle-half-dashed" },
]);

const selectedTypeLabel = computed(() => typeOptions.value.find((option) => option.value === type.value)?.label);

const reasonOptions = computed<LabelValue<string>[]>(() =>
    configStore.unavailabilityReasonsOptions.filter((option: LabelValue<string>) => !EXCLUDED_REASONS.includes(option.label))
);

const filteredReasonOptions = computed(() => reasonOptions.value.filter((option) => match(option.label, reasonSearch.value)));

const selectedReasonLabel = computed(
    () => reasonOptions.value.find((option) => option.value === reason.value)?.label ?? reason.value
);

const periodLabel = (period: DayPeriod) => t(`availability.${period}`);

const isSlotAvailable = (period: DayPeriod) => type.value === UnavailabilityType.CUSTOM && availableSlots.value[period];

const unavailablePeriods = computed(() => DAY_PERIODS.filter((period) => !availableSlots.value[period]));
const availablePeriods = computed(() => DAY_PERIODS.filter((period) => availableSlots.value[period]));
const unavailableSummary = computed(() => unavailablePeriods.value.map(periodLabel).join(" - "));
const availableSummary = computed(() => availablePeriods.value.map(periodLabel).join(" - "));

const hasUnavailablePeriod = computed(() => type.value === UnavailabilityType.FULL_DAY || unavailablePeriods.value.length > 0);

const isFormValid = computed(() => isDateRangeValid.value && !!reason.value && hasUnavailablePeriod.value);

const dateError = computed(() => {
    if (hasDateRangeError.value) return t("modals.profileUnavailability.errors.dateRange");
    if (hasTriedSubmit.value && !isDateRangeValid.value) return t("modals.profileUnavailability.errors.datesRequired");
    return "";
});
const periodsError = computed(() =>
    hasTriedSubmit.value && !hasUnavailablePeriod.value ? t("modals.profileUnavailability.errors.periodsRequired") : ""
);
const reasonError = computed(() =>
    hasTriedSubmit.value && !reason.value ? t("modals.profileUnavailability.errors.reasonRequired") : ""
);

const handleStartDatePicked = (value: string) => {
    startDate.value = value;
};

const handleEndDatePicked = (value: string) => {
    endDate.value = value;
};

const handleTypeDropdownChange = (eventName: supporttedEventNames) => {
    if (eventName === "clickaway") isTypeDropdownOpen.value = false;
};

const handleReasonDropdownChange = (eventName: supporttedEventNames) => {
    if (eventName === "clickaway") isReasonDropdownOpen.value = false;
};

const handleSelectType = (value: UnavailabilityType) => {
    type.value = value;
    isTypeDropdownOpen.value = false;
};

const handleSelectReason = (option: LabelValue<string>) => {
    reason.value = option.value;
    reasonSearch.value = "";
    isReasonDropdownOpen.value = false;
};

const handleToggleSlot = (period: DayPeriod) => {
    availableSlots.value[period] = !availableSlots.value[period];
};

const handleClose = () => {
    emit("close");
};

const handleRequestClose = () => {
    if (isSubmitting.value) return;
    handleClose();
};

// biome-ignore lint/suspicious/noExplicitAny: API error payload is untyped
const handleSubmitError = async (e: any) => {
    await props.onSubmitError?.(e);

    // biome-ignore lint/suspicious/noExplicitAny: API error payload is untyped
    const error: any = e?.errors ? Object.values(e.errors)[0] : undefined;

    if (error?.id === "ConfirmedOrder.Exists") {
        toastError(
            t("errors.ConfirmedOrder.Exists", {
                start: moment(error.data.start).format(t("date.shortFormat")),
                end: moment(error.data.end).format(t("date.shortFormat")),
                name: error.data.name,
            })
        );
    } else {
        toastError(t("errors.genericError"));
    }
};

const handleSubmit = async () => {
    hasTriedSubmit.value = true;
    const startDateIso = toCalendarDayUtcIso(startDate.value, DATE_FORMAT);
    const endDateIso = toCalendarDayUtcIso(endDate.value, DATE_FORMAT);
    if (!isFormValid.value || !startDateIso || !endDateIso) return;

    const refreshPayload: ProfileUnavailabilityRefreshPayload = {
        startDate: startDateIso,
        endDate: endDateIso,
        morning: isSlotAvailable(DayPeriod.MORNING),
        afternoon: isSlotAvailable(DayPeriod.AFTERNOON),
        evening: isSlotAvailable(DayPeriod.EVENING),
        night: isSlotAvailable(DayPeriod.NIGHT),
        reason: reason.value,
        comment: comment.value || undefined,
    };

    isSubmitting.value = true;
    try {
        await props.onBeforeSubmit?.(refreshPayload);

        await workerService.saveLongUnavailability({
            interimId: props.interimId,
            data: {
                ...refreshPayload,
                morning: !refreshPayload.morning,
                afternoon: !refreshPayload.afternoon,
                evening: !refreshPayload.evening,
                night: !refreshPayload.night,
            },
        });

        if (props.onPlanningRefresh) {
            props.onPlanningRefresh(refreshPayload);
        } else {
            props.onRefresh?.(refreshPayload);
        }

        if (!isEditMode.value) toastSuccess(t("modals.profileUnavailability.createSuccess"));
        handleClose();
    } catch (e) {
        await handleSubmitError(e);
    } finally {
        isSubmitting.value = false;
    }
};

watch(start, (newStart) => {
    if (!newStart?.isValid() || !end.value?.isValid() || !maxEnd.value) return;

    if (end.value.isBefore(newStart)) {
        endDate.value = newStart.format(DATE_FORMAT);
    } else if (end.value.isAfter(maxEnd.value)) {
        endDate.value = maxEnd.value.format(DATE_FORMAT);
    }
});
</script>

<template>
    <Modal
        class="profile-unavailability-modal"
        width="640px"
        content-overflow="visible"
        :title="isEditMode ? t('modals.profileUnavailability.editTitle') : t('modals.profileUnavailability.title')"
        :open="props.open"
        :disable-overlay-close="isSubmitting"
        close
        @close="handleRequestClose"
    >
        <template #default>
            <div class="profile-unavailability-modal__content">
                <div class="profile-unavailability-modal__section">
                    <div class="profile-unavailability-modal__item">
                        <div :class="['profile-unavailability-modal__item-label', { 'profile-unavailability-modal__item-label--error': dateError }]">
                            <i class="profile-unavailability-modal__item-label-icon ti ti-cal" />
                            <span class="typo-xxs-medium">{{ t("modals.profileUnavailability.fields.date") }}</span>
                            <span class="profile-unavailability-modal__item-label-required typo-xxs-medium">*</span>
                        </div>
                        <div class="profile-unavailability-modal__dates">
                            <DatePicker
                                class="profile-unavailability-modal__date-picker"
                                :value="startDate"
                                :format="DATE_FORMAT"
                                :min-date="minStartDate"
                                :clearable="false"
                                @date-picked="handleStartDatePicked"
                            >
                                <ComboButton variant="ghost" full-width :placeholder="!start" :error="!!dateError">
                                    {{ startLabel || t("modals.profileUnavailability.fields.startPlaceholder") }}
                                </ComboButton>
                            </DatePicker>
                            <DatePicker
                                class="profile-unavailability-modal__date-picker"
                                :value="endDate"
                                :format="DATE_FORMAT"
                                :min-date="minEndDate"
                                :max-date="maxEndDate"
                                :clearable="false"
                                @date-picked="handleEndDatePicked"
                            >
                                <ComboButton
                                    variant="ghost"
                                    icon-left="arrow-right"
                                    full-width
                                    :placeholder="!end"
                                    :error="!!dateError"
                                >
                                    {{ endLabel || t("modals.profileUnavailability.fields.endPlaceholder") }}
                                </ComboButton>
                            </DatePicker>
                        </div>
                        <span v-if="dateError" class="profile-unavailability-modal__field-error typo-xxs-regular">{{ dateError }}</span>
                    </div>

                    <div class="profile-unavailability-modal__item">
                        <div class="profile-unavailability-modal__item-label">
                            <i class="profile-unavailability-modal__item-label-icon ti ti-circle-full-dashed" />
                            <span class="typo-xxs-medium">{{ t("modals.profileUnavailability.fields.type") }}</span>
                        </div>
                        <ControlledDropdown :styles="TYPE_DROPDOWN_STYLES" :open="isTypeDropdownOpen" @change="handleTypeDropdownChange">
                            <template #trigger-zone>
                                <ComboButton variant="ghost" @click="isTypeDropdownOpen = !isTypeDropdownOpen">
                                    {{ selectedTypeLabel }}
                                </ComboButton>
                            </template>
                            <template #dropdown-content>
                                <DropdownItem
                                    v-for="option of typeOptions"
                                    :key="option.value"
                                    :label="option.label"
                                    :icon-left="option.icon"
                                    v-bind="option.value === type ? SELECTED_OPTION_PROPS : undefined"
                                    @click="handleSelectType(option.value)"
                                />
                            </template>
                        </ControlledDropdown>
                    </div>

                    <template v-if="type === UnavailabilityType.CUSTOM">
                        <div class="profile-unavailability-modal__item">
                            <div class="profile-unavailability-modal__item-label" />
                            <div class="profile-unavailability-modal__slots">
                                <CheckboxLabel
                                    v-for="period of DAY_PERIODS"
                                    :key="period"
                                    size="sm"
                                    :label="periodLabel(period)"
                                    :checked="availableSlots[period]"
                                    @input="handleToggleSlot(period)"
                                />
                            </div>
                        </div>
                        <div v-if="unavailablePeriods.length" class="profile-unavailability-modal__item">
                            <div class="profile-unavailability-modal__item-label" />
                            <span class="profile-unavailability-modal__summary typo-xxs-medium">
                                {{ t("modals.profileUnavailability.summary.unavailable", { periods: unavailableSummary }) }}
                            </span>
                        </div>
                        <div v-if="availablePeriods.length" class="profile-unavailability-modal__item">
                            <div class="profile-unavailability-modal__item-label" />
                            <span class="profile-unavailability-modal__summary typo-xxs-medium">
                                {{ t("modals.profileUnavailability.summary.available", { periods: availableSummary }) }}
                            </span>
                        </div>
                        <div v-if="periodsError" class="profile-unavailability-modal__item">
                            <div class="profile-unavailability-modal__item-label" />
                            <Alert class="profile-unavailability-modal__periods-error" variant="error" :message="periodsError" />
                        </div>
                    </template>
                </div>

                <Separator />

                <div class="profile-unavailability-modal__section">
                    <div class="profile-unavailability-modal__item">
                        <div :class="['profile-unavailability-modal__item-label', { 'profile-unavailability-modal__item-label--error': reasonError }]">
                            <i class="profile-unavailability-modal__item-label-icon ti ti-selector" />
                            <span class="typo-xxs-medium">{{ t("modals.profileUnavailability.fields.reason") }}</span>
                            <span class="profile-unavailability-modal__item-label-required typo-xxs-medium">*</span>
                        </div>
                        <ControlledDropdown
                            :styles="REASON_DROPDOWN_STYLES"
                            max-height="260px"
                            :open="isReasonDropdownOpen"
                            @change="handleReasonDropdownChange"
                        >
                            <template #trigger-zone>
                                <ComboButton
                                    class="profile-unavailability-modal__reason-trigger"
                                    variant="ghost"
                                    :placeholder="!reason"
                                    :error="!!reasonError"
                                    @click="isReasonDropdownOpen = !isReasonDropdownOpen"
                                >
                                    {{ selectedReasonLabel || t("modals.profileUnavailability.fields.reasonPlaceholder") }}
                                </ComboButton>
                            </template>
                            <template #dropdown-content>
                                <DropdownInput
                                    v-model="reasonSearch"
                                    noicon
                                    :focused="isReasonDropdownOpen"
                                    :placeholder="t('modals.profileUnavailability.fields.reasonSearchPlaceholder')"
                                />
                                <DropdownSeparator />
                                <DropdownItem
                                    v-for="option of filteredReasonOptions"
                                    :key="option.value"
                                    :label="option.label"
                                    v-bind="option.value === reason ? SELECTED_OPTION_PROPS : undefined"
                                    @click="handleSelectReason(option)"
                                />
                                <DropdownItem v-if="!filteredReasonOptions.length" :label="t('global.noResults')" disabled no-hover />
                            </template>
                        </ControlledDropdown>
                        <span v-if="reasonError" class="profile-unavailability-modal__field-error typo-xxs-regular">{{ reasonError }}</span>
                    </div>

                    <div class="profile-unavailability-modal__item profile-unavailability-modal__item--top">
                        <div class="profile-unavailability-modal__item-label">
                            <i class="profile-unavailability-modal__item-label-icon ti ti-answer-text" />
                            <span class="typo-xxs-medium">{{ t("modals.profileUnavailability.fields.comment") }}</span>
                        </div>
                        <TextareaInputAlt
                            v-model="comment"
                            class="profile-unavailability-modal__comment"
                            :placeholder="t('modals.profileUnavailability.fields.commentPlaceholder')"
                            :rows="1"
                            textarea-font-weight="500"
                            auto-resize
                            no-border
                        />
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="profile-unavailability-modal__footer">
                <Link type="neutral" :label="t('global.actions.cancel')" :disabled="isSubmitting" @click="handleRequestClose" />
                <Button
                    size="sm"
                    :label="t('global.actions.save')"
                    :loading="isSubmitting"
                    @click="handleSubmit"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.profile-unavailability-modal {
    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
    }

    &__section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
    }

    &__item {
        display: flex;
        align-items: center;
        min-height: 28px;

        &--top {
            align-items: flex-start;

            .profile-unavailability-modal__item-label {
                height: 28px;
            }
        }

        &-label {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            gap: var(--spacing-sm, 6px);
            width: 120px;
            color: var(--text-sub-600, #494e61);

            &-icon {
                font-size: 14px;
                color: var(--icon-sub-500, #636778);
            }

            &-required {
                color: var(--blue-base, #2051f8);
            }

            &--error,
            &--error .profile-unavailability-modal__item-label-icon {
                color: var(--red-base, #e51940);
            }
        }
    }

    &__dates {
        display: flex;
        gap: 2px;
    }

    &__date-picker {
        width: 124px;

        :deep(.ds-combo-button) {
            justify-content: flex-start;
        }
    }

    &__slots {
        display: flex;
        align-items: center;
        gap: var(--spacing-3xl, 24px);
        height: 28px;
        padding: 0 var(--spacing-md, 8px);
    }

    &__summary {
        padding: 0 var(--spacing-md, 8px);
        color: var(--text-sub-600, #494e61);
    }

    &__field-error {
        margin-left: var(--spacing-md, 8px);
        padding: var(--spacing-xs, 4px) var(--spacing-md, 8px);
        border: 0.5px solid var(--stroke-soft-200, #dbdfe6);
        border-radius: var(--radius-sm, 6px);
        background: var(--bg-white-0, #ffffff);
        box-shadow: var(--regular-shadow-xs, 0 1px 2px #dbdfe640);
        color: var(--text-sub-600, #494e61);
        min-width: 0;
    }

    &__periods-error.alert-component {
        width: 384px;
        border-radius: var(--radius-md, 8px);
    }

    &__reason-trigger {
        min-width: 160px;
        justify-content: flex-start;
    }

    &__comment {
        flex: 1 0 0;
        min-width: 0;

        :deep(textarea.noBorder) {
            padding: 5px var(--spacing-lg, 12px);
            border-radius: var(--radius-md, 8px);
            resize: none;
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--spacing-3xl, 24px);
    }
}
</style>
